import React, { useState, useEffect } from 'react';
import { X, Share, PlusSquare, ChevronDown } from 'lucide-react';

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent || '');
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

const DISMISS_KEY = 'mk_pwa_install_dismissed';
const DISMISS_DAYS = 7;

function wasDismissedRecently() {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  const diff = Date.now() - parseInt(ts, 10);
  return diff < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

// --- Full modal with visual instructions ---
export function IOSInstallModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100] flex items-end justify-center" onClick={onClose}>
      <div className="bg-gray-900 w-full max-w-md rounded-t-3xl animate-in slide-in-from-bottom duration-300"
        onClick={e => e.stopPropagation()} data-testid="ios-install-modal">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-white font-bold text-lg">Instalar Mallorca Klan</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-1" data-testid="ios-install-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          <p className="text-gray-300 text-sm leading-relaxed">
            Instala la app en tu pantalla de inicio para acceder mas rapido y tener la experiencia completa.
          </p>

          {/* Step 1 */}
          <div className="flex gap-4 items-start">
            <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0 border border-gold/20">
              <span className="text-gold font-bold text-sm">1</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Pulsa el boton Compartir</p>
              <p className="text-gray-500 text-xs mt-1">
                El icono <Share className="inline w-3.5 h-3.5 text-blue-400 -mt-0.5" /> que aparece abajo en Safari
              </p>
              <div className="mt-2 bg-gray-800 rounded-lg p-3 flex items-center justify-center">
                <div className="flex items-center gap-2 bg-blue-500/20 px-4 py-2 rounded-lg border border-blue-500/30">
                  <Share className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-400 text-sm font-medium">Compartir</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-4 items-start">
            <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0 border border-gold/20">
              <span className="text-gold font-bold text-sm">2</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Selecciona "Anadir a pantalla de inicio"</p>
              <p className="text-gray-500 text-xs mt-1">Desliza hacia abajo en el menu hasta encontrarlo</p>
              <div className="mt-2 bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-3 bg-gray-700/50 px-3 py-2.5 rounded-lg border border-gray-600/30">
                  <PlusSquare className="w-5 h-5 text-gray-300" />
                  <span className="text-white text-sm">Anadir a pantalla de inicio</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-4 items-start">
            <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0 border border-gold/20">
              <span className="text-gold font-bold text-sm">3</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Pulsa "Anadir" para confirmar</p>
              <p className="text-gray-500 text-xs mt-1">Ya tendras Mallorca Klan en tu pantalla como una app</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800">
          <button onClick={onClose}
            className="w-full py-3 bg-gold text-black font-bold rounded-xl text-sm hover:bg-gold/90 transition-colors"
            data-testid="ios-install-understood">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Auto banner for iOS users ---
export function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (isIOS() && !isInStandaloneMode() && !wasDismissedRecently()) {
      const timer = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  const openInstructions = () => {
    setVisible(false);
    setShowModal(true);
  };

  if (!visible && !showModal) return null;

  return (
    <>
      {visible && (
        <div className="fixed bottom-0 left-0 right-0 z-[90] p-3 animate-in slide-in-from-bottom duration-500"
          data-testid="ios-install-banner">
          <div className="max-w-md mx-auto bg-gray-900 border border-gray-700 rounded-2xl p-4 shadow-2xl shadow-black/50">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0 border border-gold/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_espanol-mode-15/artifacts/jlomgnz6_WhatsApp%20Image%202025-11-16%20at%2010.07.15.jpeg"
                  alt="MK" className="w-10 h-10 rounded-lg"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm">Instala Mallorca Klan</p>
                <p className="text-gray-400 text-xs mt-0.5">Accede mas rapido desde tu pantalla de inicio</p>
              </div>
              <button onClick={dismiss} className="text-gray-600 hover:text-gray-400 p-1 -mt-1 -mr-1" data-testid="ios-banner-dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={openInstructions}
                className="flex-1 py-2.5 bg-gold text-black font-bold rounded-xl text-xs hover:bg-gold/90 transition-colors"
                data-testid="ios-banner-install">
                Ver como instalar
              </button>
              <button onClick={dismiss}
                className="px-4 py-2.5 text-gray-400 text-xs hover:text-white transition-colors"
                data-testid="ios-banner-later">
                Ahora no
              </button>
            </div>
          </div>
        </div>
      )}
      <IOSInstallModal open={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}

// --- Button for settings/profile ---
export function InstallAppButton() {
  const [showModal, setShowModal] = useState(false);

  // Show on iOS not installed, or Android (for beforeinstallprompt)
  const isiOS = isIOS();
  const isStandalone = isInStandaloneMode();

  if (isStandalone) return null;

  return (
    <>
      <button onClick={() => setShowModal(true)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl transition-colors group"
        data-testid="btn-install-app">
        <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center border border-gold/20 group-hover:bg-gold/20 transition-colors">
          <PlusSquare className="w-4 h-4 text-gold" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-white text-sm font-medium">Instalar app en tu movil</p>
          <p className="text-gray-500 text-xs">{isiOS ? 'Anadir a pantalla de inicio' : 'Instalar como aplicacion'}</p>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-600 -rotate-90" />
      </button>
      <IOSInstallModal open={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}

export default IOSInstallBanner;
